import React from 'react'
import Helmet from 'react-helmet'
import get from 'lodash/get'
import { graphql } from 'gatsby'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'

import { CONFIG } from '../config'

import Author from '../components/Author'
import BlogHeader from '../components/BlogPostHeader'
import BlogSocial from '../components/BlogSocial'
import Layout from '../components/layout'
import SchemaOrgJSONLD from '../components/JSONLD'

import * as styles from './blog-post.module.scss'

// AdvancedFormat extends `dayjs().format` API to supply more format options.
// E.g. `dayjs().format('Q Do k kk X x')`
dayjs.extend(advancedFormat)

const BlogPostTemplate = (props) => {
  const post = get(props, 'data.contentfulBlogPost')
  const currentUrl = `${CONFIG.BLOG_URL}/${post.slug}`
  const metaTitle = post.metaTitle || post.title
  const metaDescription = get(post, 'metaDescription', '')
  // Use the metaImage if there is one or fall back to heroImage

  let metaImageSrc = post.metaImage?.file?.url
    ? post.metaImage.file.url
    : post.heroImage.file.url

  metaImageSrc = `https://${metaImageSrc.split('?')[0]}`

  // for when gatsby builds and the window obj is not available
  if (
    typeof window !== 'undefined' &&
    post.slug === ('student-sim' || 'student-sim/')
  ) {
    window.location = `${CONFIG.MARKETING_URL}student-sim/`
  }

  if (post.slug === ('student-sim' || 'student-sim/')) {
    return null
  }

  const seo = {
    postUrl: currentUrl,
    postTitle: post.title,
    publishDate: post.publishDate,
    updatedAt: post.updatedAt,
    author: post.author.name,
    description: metaDescription,
    imageUrl: `https://${post.heroImage.file.url}`,
  }

  return (
    <Layout location={props.location}>
      <div
        style={{ background: '#fff' }}
        itemScope
        itemType="http://schema.org/Article"
        role="main"
      >
        <SchemaOrgJSONLD {...seo} />
        <Helmet>
          <title>{`SMARTY blog | ${post.title}`}</title>
          <meta name="title" content={metaTitle} />
          <meta name="description" content={metaDescription} />
          <link
            href="https://fonts.googleapis.com/css?family=Montserrat:300,400,500,700"
            rel="stylesheet"
          />
          <link
            rel="shortcut icon"
            sizes="16x16 32x32"
            type="image/png"
            href={`${CONFIG.MARKETING_URL}favicon.png`}
          ></link>
          <link
            rel="apple-touch-icon"
            href={`${CONFIG.MARKETING_URL}apple-touch-icon.png`}
          />
          <link rel="canonical" href={currentUrl} />
          <link
            rel="sitemap"
            type="application/xml"
            href={`${CONFIG.MARKETING_URL}sitemap.xml`}
          />
        </Helmet>

        <BlogHeader {...post} />
        <div className={styles.Container}>
          <div className={styles.Content}>
            <div className={styles.ColLeft}>
              <BlogSocial
                currentUrl={currentUrl}
                metaTitle={metaTitle}
                metaDescription={metaDescription}
                metaImageSrc={metaImageSrc}
              />
            </div>
            <div className={styles.ColRight}>
              <div
                dangerouslySetInnerHTML={{
                  __html: post.body.childMarkdownRemark.html,
                }}
              />
            </div>
          </div>
          <div className={styles.AuthorWrap}>
            <Author
              {...post.author}
              date={dayjs(post.publishDate).format('MMMM Do, YYYY')}
            />
          </div>
          <div className={styles.MostRecentPosts} />
        </div>
      </div>
    </Layout>
  )
}

export default BlogPostTemplate

export const pageQuery = graphql`
  query BlogPostBySlug($slug: String!) {
    contentfulBlogPost(slug: { eq: $slug }) {
      title
      metaTitle
      metaDescription
      metaImage {
        title
        description
        file {
          url
        }
      }
      slug
      publishDate
      updatedAt
      backgroundColour {
        hex
      }
      heroImage {
        title
        description
        file {
          url
        }
      }
      author {
        name
        title
        image {
          title
          description
          file {
            url
          }
        }
      }
      body {
        childMarkdownRemark {
          html
        }
      }
    }
  }
`
